import { memo, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import close_img from 'assets/close.svg';
import moment from 'moment';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';

import TrackingAPI from '../../api/TrackingAPI';
import NiceModal from "../../components/modals/NiceModal";
import { formatDateToFullFormatWithSeconds, FULL_DATE_FORMAT_WITH_SECONDS, now } from '../../utils/dates';
import ImageModalSection from './components/ImageModalSection';
import MainContentModalSection from './components/MainContentModalSection';

import './styles.css';

const CloseButton = styled.div`
  @media (max-width: 430px) {
    position: fixed;
    right: 24px;
    top: 24px;
    z-index: 1000;
    cursor: pointer;

    img {
      width: 20px;
      height: 20px;
    }
  }
`;

interface IStripeOfferBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  analyticsDataRenderReason?: string;
}

const TrialOfferModal: React.FC<IStripeOfferBannerModalProps> = (props) => {
  const { isOpen, onClose, analyticsDataRenderReason = 'open_app' } = props;

  const { currentUser } = useMappedState(
    state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(currentUserId);
      return {
        currentUser
      };
    }
  );

  const subscriptionEndDate = formatDateToFullFormatWithSeconds(currentUser?.subscription?.currentPeriodEndDate ?? '');

  const expireSubscriptionDate = moment(subscriptionEndDate, FULL_DATE_FORMAT_WITH_SECONDS);
  const nowTime = now();
  const isExpiredSub = nowTime.isAfter(expireSubscriptionDate);

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const trackEvent = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const analyticData = {
        rendered_for_reason: analyticsDataRenderReason,
      };

      try {
        TrackingAPI.trackEvent('stripe_email_offer_banner_modal_rendered', analyticData);
      } catch (error) {
        throw error;
      }
    };

    trackEvent();
  }, [analyticsDataRenderReason, isExpiredSub]);

  return (
    <NiceModal
      customCloseButton={isMobile ? <CloseButton><img src={close_img} alt="Close" /></CloseButton> : null}
      closeOnEsc={!isMobile}
      isVisible={isOpen}
      onClose={onClose}
      canClose
    >
      <Wrapper>
        {!isMobile && <ImageModalSection />}
        <MainContentModalSection />
      </Wrapper>
    </NiceModal>
  );
};

export default memo(TrialOfferModal);

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 65vh;
  min-height: 548px;
  max-height: 548px;
  max-width: 880px;
  min-width: 825px;

  @media (max-width: 768px) {
    min-height: unset;
    min-width: unset;
    height: 90vh;
    width: 100%;
  }

  @media (max-height: 700px) {
    min-height: unset;
    min-width: unset;
    height: 96vh;
  }
`;
